import { MotionConfig } from "motion/react";
import { useEffect, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";

import { Skeleton } from "../../components/ui/skeleton";
import { ROUTES } from "../../shared/config/routes";
import { Button } from "./components/ui/button";
import { BackgroundUploads } from "./components/BackgroundUploads";
import { TaskUrlSync } from "./components/TaskUrlSync";
import { NewTaskModal } from "./features/client/NewTaskModal";
import { ClientTaskModal } from "./features/client/TaskModal";
import { EditorTaskModal } from "./features/editor/EditorTask";
import { spring } from "./lib/motion";
import { MONTAGE_TOASTER } from "./lib/notify";
import { bindRouter, useStore } from "./store";

/** Раз в сколько перечитывать задачи: действия второй стороны должны появляться сами. */
const REFRESH_MS = 30_000;

/**
 * Ссылка на публикацию (`?task=&item=`, например «Вернуться в Монтаж» после
 * публикации) переживает переход в свой раздел: монтажёр с /montage попадает
 * в «Мои задачи» на тот же ролик. У «Модерации» эти параметры свои — туда нет.
 */
function carry(search: string, to: string): string {
  if (to === ROUTES.MODERATION) return "";
  const from = new URLSearchParams(search);
  const next = new URLSearchParams();
  for (const key of ["task", "item"]) {
    const value = from.get(key);
    if (value) next.set(key, value);
  }
  const s = next.toString();
  return s ? `?${s}` : "";
}

/**
 * Контейнер тостов раздела. Стоит и на экранах без MontageRoot («Ролики B2G»,
 * «Отработано»): иначе их тосты, в том числе с «Отменить», не показывались.
 */
export function MontageToaster() {
  return (
    <Toaster
      id={MONTAGE_TOASTER}
      className="montage-root"
      // Сверху по центру: внизу у модалок панель с «Принять»/«На правки», и
      // тост с «Отменить» 5 секунд закрывал её (модератор Шахноза, 16.10).
      // Касания тост пропускает насквозь — ловит только кнопка «Отменить».
      position="top-center"
      offset={16}
      mobileOffset={12}
      gap={10}
      visibleToasts={4}
      toastOptions={{ unstyled: true, className: "pointer-events-none!" }}
      style={{ ["--width" as string]: "560px" }}
    />
  );
}

/**
 * Обёртка раздела «Монтаж» внутри сайта.
 *
 * Держит то, что в прототипе было в App: загрузку людей и задач, модалки задач
 * и постановки, контейнер тостов. Демо-панели и сайдбара прототипа здесь нет —
 * навигация сайтовая.
 *
 * Высота — окно минус шапка сайта и его отступы: колонки доски и календарь
 * по прототипу помещаются целиком, без прокрутки страницы. Уже 1040 px макет
 * не сжимается — ниже появляется горизонтальная прокрутка.
 */
/** `people` — экран для постановщика и монтажёра сразу («Видео»), без окон задач. */
export function MontageRoot({ expect, children }: { expect: "client" | "editor" | "moderator" | "people"; children: ReactNode }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const loaded = useStore((s) => s.loaded);
  const loadError = useStore((s) => s.loadError);
  const role = useStore((s) => s.role);
  const load = useStore((s) => s.load);
  const refresh = useStore((s) => s.refresh);

  useEffect(() => {
    bindRouter((path) => navigate(path));
  }, [navigate]);

  useEffect(() => {
    if (!loaded) void load();
  }, [loaded, load]);

  useEffect(() => {
    if (!loaded || loadError) return;
    const timer = window.setInterval(() => void refresh(), REFRESH_MS);
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [loaded, loadError, refresh]);

  let body: ReactNode = children;
  if (!loaded) {
    body = (
      <div className="flex h-full flex-col gap-5">
        <Skeleton className="h-9 w-56" />
        <div className="flex min-h-0 flex-1 gap-2.5">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-full flex-1 rounded-[28px]" />)}
        </div>
      </div>
    );
  } else if (loadError) {
    body = <p className="text-sm text-muted-foreground">Не удалось загрузить раздел: {loadError}</p>;
  } else if (expect === "people" ? role !== "client" && role !== "editor" : role !== expect) {
    // Сюда попадают по прямой ссылке: постановщику нужна доска, монтажёру — свои
    // задачи, модератору — «Модерация». Не тупик: кнопка ведёт в нужный раздел.
    const where =
      role === "client" ? { text: "Публикации отдела — на доске «Монтаж».", to: ROUTES.MONTAGE, label: "Открыть «Монтаж»" }
      : role === "editor" ? { text: "Ваши публикации — в разделе «Мои задачи».", to: ROUTES.MY_TASKS, label: "Открыть «Мои задачи»" }
      : role === "moderator" ? { text: "Ролики на проверку — в разделе «Модерация».", to: ROUTES.MODERATION, label: "Открыть «Модерацию»" }
      : null;
    const what =
      expect === "client" ? "Доска монтажа — для контент-мейкеров." : expect === "editor" ? "«Мои задачи» — экран монтажёра." : expect === "people" ? "«Видео» — для монтажёров, контент-мейкеров и руководителя." : "«Модерация» — экран модератора отдела.";
    body = (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-muted-foreground">{what} {where?.text}</p>
        {where && <Button onClick={() => navigate(`${where.to}${carry(search, where.to)}`)}>{where.label}</Button>}
      </div>
    );
  }

  return (
    <MotionConfig reducedMotion="user" transition={spring.smooth}>
      <div className={expect === "moderator" ? "-mx-4 px-4 md:-mx-6 md:px-6" : "-mx-4 overflow-x-auto px-4 md:-mx-6 md:px-6"}>
        <div className={expect === "moderator"
          ? "montage-root min-h-[calc(100svh-8rem)] pb-[max(1rem,env(safe-area-inset-bottom))]"
          : "montage-root h-[calc(100svh-3.5rem-3rem)] min-h-[600px] min-w-[1040px]"}>{body}</div>
      </div>
      {loaded && !loadError && role === expect && (
        <>
          {(expect === "client" || expect === "editor") && <TaskUrlSync />}
          <ClientTaskModal />
          <EditorTaskModal />
          <NewTaskModal />
        </>
      )}
      <div className="montage-root"><BackgroundUploads /></div>
      <MontageToaster />
    </MotionConfig>
  );
}
